import React, { useState } from 'react';
import { LoadingBounce, useDialogs } from '@michelin/acid-components';
import { useTranslation } from '@michelin/central-provider';
import { Link } from '@material-ui/core';
import { getOptionLabel } from '@michelin/select-options-provider';
import { useMutation } from 'react-query';
import uuid from 'uuid';
import {
  Contact,
  ContactListItem,
  MechanicCertification,
  SPPContactTypeLevelsEnum,
  SPPContactTypeRoleAreasEnum,
  SPPContactTypeServicesEnum,
} from '../../../Types';
import { useContact } from '../../../hooks/useContactQueries';
import { PlainMessage } from '../../PlainMessage';
import { ContactDetailModal } from '../DetailModal';
import ContactsListModal from '../ListModal';
import { MechanicCertificationsDetail } from '../MechanicCertifications/Detail';
import { getNewContact, manageContactSave } from '../Utils';
import { addCertificate, addContactTypeRoleArea } from './utils';

interface SelectContactProps {
  contactId?: string,
  shipToNumber: string,
  billToNumber: string,
  countryCode: string,
  certification_type: string,
  certification_job: string,
  onChange?: (contact: Contact | null) => void,
}

interface ViewContactProps {
  contactId: string,
  shipToNumber: string,
}

interface AssignCreateContactProps {
  shipToNumber: string,
  billToNumber: string,
  countryCode: string,
  certification_type: string,
  certification_job: string,
  onChange?: (contact: Contact | null) => void,
}

function ViewContact(props: ViewContactProps) {
  const {
    contactId, shipToNumber,
  } = props;

  const { t } = useTranslation();

  const contactQuery = useContact({
    stNumber: shipToNumber,
    contactId,
    defaultData: undefined,
    queryKey: 'select-contact',
  });

  if (contactQuery.isLoading || contactQuery.isFetching) {
    return <LoadingBounce style={{ height: '70vh' }} />;
  }

  if (contactQuery.isError) {
    return (
      <PlainMessage
        title={t('Database Error')}
        messages={t('Application could not load contact data.\nPlease try again later or contact support if the error persists').split('\n')}
      />
    );
  }

  return (
    <h3 style={{ marginBottom: 10, marginTop: 10 }}>
      {contactQuery.data.first_name}
      {' '}
      {contactQuery.data.last_name}
    </h3>
  );
}

function AssignCreateContact(props: AssignCreateContactProps) {
  const {
    shipToNumber, billToNumber, countryCode, certification_type, certification_job, onChange,
  } = props;

  const certificate = {} as MechanicCertification;
  certificate.id = uuid.v4();
  certificate.type = certification_type;
  certificate.job = certification_job;
  let defaultData: Contact = getNewContact();
  defaultData = addContactTypeRoleArea({
    contact: defaultData,
    service: SPPContactTypeServicesEnum.commercial,
    roleArea: SPPContactTypeRoleAreasEnum.mechanic,
    level: SPPContactTypeLevelsEnum.general,
    location: shipToNumber,
  });

  const [selectedContact, setSelectedContact] = useState<Contact | ContactListItem | null>(null);
  const [creating, setCreating] = useState<boolean>(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [assigning, setAssigning] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);

  const { t } = useTranslation();
  const { errorDialog } = useDialogs();

  const contactQuery = useContact({
    stNumber: shipToNumber,
    contactId: selectedContact?.id || '',
    defaultData: selectedContact?.id ? undefined : {} as Contact,
    queryKey: `query-key${selectedContact?.id}`,
  });

  const { mutateAsync } = useMutation(manageContactSave);

  const typeLabel = (countryCode === 'us')
    ? getOptionLabel('spp_mechanical_certifications_services_us', certificate.type)
    : getOptionLabel('spp_mechanical_certifications_services_ca', certificate.type);

  const jobLabel = (countryCode === 'us')
    ? getOptionLabel('spp_mechanical_certifications_jobs_mechanical_us', certificate.job)
    : getOptionLabel('spp_mechanical_certifications_jobs_mechanical_ca', certificate.job);

  const certificateName = `${typeLabel} ${jobLabel}`;

  const contactName = selectedContact ? `${selectedContact.first_name} ${selectedContact.last_name}` : '';

  if (contactQuery.isError) {
    errorDialog(
      t('Application could not load contact data.\nPlease try again later or contact support if the error persists').split('\n'),
      t('Database Error'),
      t('Ok'),
      () => {
        setSelectedContact(null);
      },
    );
  }

  return (
    <>
      {(saving)
        ? <LoadingBounce />
        : (
          <>
            <Link
              style={{ cursor: 'pointer' }}
              onClick={() => {
                setAssigning(true);
              }}
            >
              {`[${t('Assign')}]`}
            </Link>
            &nbsp;
            <Link
              style={{ cursor: 'pointer' }}
              onClick={() => {
                setCreating(true);
              }}
            >
              {`[${t('Create')}]`}
            </Link>
          </>
        )}

      {assigning && !selectedContact && !saving ? (
        <ContactsListModal
          onClose={() => {
            setAssigning(false);
            onChange?.(null);
          }}
          title={t('Choose {{certificateName}} Contact', { certificateName })}
          stNumber={shipToNumber}
          btNumber={billToNumber}
          contactLevel="ST"
          customActions={[
            {
              id: `${certificate.type}_${certificate.job}`,
              label: t('Choose'),
              action: (id: string, contact: ContactListItem) => {
                if (contact.missing_info) {
                  setEditing(contact.id);
                } else setSelectedContact(contact);
              },
            },
          ]}
        />
      ) : null}
      {editing && !selectedContact && !saving ? (
        <ContactDetailModal
          title={t('Complete Required Contact Information', { certificateName })}
          defaultData={defaultData}
          onClose={() => {
            setEditing(null);
                        onChange?.(null);
          }}
          onSave={(contact: Contact) => {
            setEditing(null);
            setSelectedContact(contact);
          }}
          countryCode={countryCode}
          action="edit"
          contactId={editing}
        />
      ) : null}
      {creating && !selectedContact && !saving ? (
        <ContactDetailModal
          title={t('Create {{certificateName}} Contact', { certificateName })}
          defaultData={defaultData}
          onClose={() => {
            setCreating(false);
                        onChange?.(null);
          }}
          onSave={(contact: Contact) => {
            setCreating(false);
            setSelectedContact(contact);
          }}
          countryCode={countryCode}
        />
      ) : null}
      {selectedContact && !saving ? (
        <MechanicCertificationsDetail
          title={t('Upload {{certificateName}} Certificate for {{contactName}}', {
            certificateName,
            contactName,
          })}
          fileOnly
          maxWidth={false}
          action="edit"
          defaultValues={certificate}
          onSave={async (data: MechanicCertification[]) => {
            try {
              setSaving(true);
              let newData = addCertificate(contactQuery.data, data.shift());
              newData = addContactTypeRoleArea({
                contact: newData,
                service: SPPContactTypeServicesEnum.commercial,
                roleArea: SPPContactTypeRoleAreasEnum.mechanic,
                level: SPPContactTypeLevelsEnum.general,
                location: shipToNumber,
              });
              const response = await mutateAsync({
                locationNumber: shipToNumber,
                contact: newData,
                contactId: newData.id,
                basicFields: false,
              });
              setSaving(false);
              if (response.status) {
                errorDialog(
                  t('Application could not load contact data.\nPlease try again later or contact support if the error persists').split('\n'),
                  t('Database Error'),
                  t('Ok'),
                  () => {
                    setSelectedContact(null);
                  },
                );
              } else {
                setCreating(false);
                setAssigning(false);
                setSelectedContact(null);
                onChange?.(response.data);
              }
            } catch (e) {
              setSaving(false);
              errorDialog(
                t('Application could not load contact data.\nPlease try again later or contact support if the error persists').split('\n'),
                t('Database Error'),
                t('Ok'),
                () => {
                  setSelectedContact(null);
                },
              );
            }
          }}
          onClose={() => {
            setSelectedContact(null);
          }}
        />
      ) : null}
    </>
  );
}

function SelectContact(props: SelectContactProps) {
  const {
    contactId, shipToNumber, billToNumber, countryCode, certification_type, certification_job, onChange,
  } = props;

  if (contactId) return <ViewContact contactId={contactId} shipToNumber={shipToNumber} />;

  return (
    <AssignCreateContact
      shipToNumber={shipToNumber}
      billToNumber={billToNumber}
      countryCode={countryCode}
      certification_type={certification_type}
      certification_job={certification_job}
      onChange={(contact) => { onChange?.(contact); }}
    />
  );
}

export {
  SelectContact,
};
